import { useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import { useAuth, useClassLearningProgress } from "@bw/shared/hooks";
import type { Schema } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { AcademyLearnerWorkspace } from "../components/AcademyLearnerWorkspace";

type Progress = Schema["ClassLearningProgress"];
const markLabels = {
  present: "Present",
  late: "Late",
  absent: "Absent",
  unmarked: "Unmarked",
} as const;

/** This Player surface never edits teaching records, even for dual-role users. */
export function LearningProgressPage() {
  const { user, loading } = useAuth();
  const { facilityId = "", programId = "", enrollmentId = "" } = useParams();
  if (loading || !user)
    return (
      <Page title="Feedback and attendance">
        <Typography role="status">
          {loading
            ? "Loading learning records…"
            : "Sign in to view learning records."}
        </Typography>
      </Page>
    );
  return (
    <ScopedLearningProgressPage
      key={`${user.uid}:${programId}:${enrollmentId}`}
      facilityId={facilityId}
      programId={programId}
      enrollmentId={enrollmentId}
    />
  );
}

function ScopedLearningProgressPage({
  facilityId,
  programId,
  enrollmentId,
}: {
  facilityId: string;
  programId: string;
  enrollmentId: string;
}) {
  const [offset, setOffset] = useState(0);
  const query = useClassLearningProgress(programId, enrollmentId, { offset });
  return (
    <Page title="Feedback and attendance">
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Button component={RouterLink} size="small" to="/me/classes">
          My classes
        </Button>
        <Button
          component={RouterLink}
          size="small"
          to={`/classes/${facilityId}/${programId}`}
        >
          Back to class
        </Button>
      </Stack>
      <AcademyLearnerWorkspace
        programId={programId}
        enrollmentId={enrollmentId}
      />
      {query.isError ? (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={() => void query.refetch()}>
              Retry
            </Button>
          }
        >
          We couldn&apos;t load this learning record. Your access may have
          changed.
        </Alert>
      ) : !query.data ? (
        <Typography role="status">Loading learning records…</Typography>
      ) : (
        <ProgressContent data={query.data} onPage={setOffset} />
      )}
    </Page>
  );
}

function ProgressContent({
  data,
  onPage,
}: {
  data: Progress;
  onPage: (offset: number) => void;
}) {
  const dates = useMemo(() => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    };
    try {
      return {
        zone: data.timezone,
        formatter: new Intl.DateTimeFormat("en-US", {
          ...options,
          timeZone: data.timezone,
        }),
      };
    } catch {
      return {
        zone: "UTC (class timezone unavailable)",
        formatter: new Intl.DateTimeFormat("en-US", {
          ...options,
          timeZone: "UTC",
        }),
      };
    }
  }, [data.timezone]);
  function time(value: string): string {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime())
      ? dates.formatter.format(parsed)
      : "Time unavailable";
  }
  const counts = [
    ["Entitled sessions", data.counts.entitled_count],
    ["Present", data.counts.present_count],
    ["Late", data.counts.late_count],
    ["Absent", data.counts.absent_count],
    ["Unmarked", data.counts.unmarked_count],
    ["Cancelled", data.counts.cancelled_count],
  ] as const;
  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography component="h2" variant="h5">
          {data.class_label.trim() || "Class name unavailable"}
        </Typography>
        <Typography>
          {data.track_label.trim() || "Track name unavailable"}
        </Typography>
        {data.learner_display_name ? (
          <Typography color="text.secondary">
            Learner: {data.learner_display_name}
          </Typography>
        ) : null}
        <Typography variant="body2" color="text.secondary">
          Times shown in {dates.zone}.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Learner posting deadline: {time(data.learner_posting_deadline)}.
          Feedback and attendance history remain available afterward.
        </Typography>
      </Stack>
      <Stack
        component="dl"
        direction="row"
        spacing={3}
        sx={{ m: 0, flexWrap: "wrap", rowGap: 1.5 }}
      >
        {counts.map(([label, count]) => (
          <Box key={label}>
            <Typography component="dt" variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography component="dd" sx={{ m: 0 }}>
              {count}
            </Typography>
          </Box>
        ))}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Unmarked means attendance has not been recorded.
      </Typography>
      {data.occurrences.length === 0 ? (
        <Typography role="status">
          {data.total === 0
            ? "No session history for this enrollment."
            : "No sessions on this page."}
        </Typography>
      ) : (
        data.occurrences.map((occurrence) => (
          <Stack
            component="section"
            key={occurrence.entitlement_id}
            spacing={1}
            sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}
          >
            <Typography component="h3" variant="h6">
              {time(occurrence.effective_start)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ends {time(occurrence.effective_end)}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                size="small"
                variant="outlined"
                label={markLabels[occurrence.attendance]}
              />
              {occurrence.state !== "scheduled" ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    occurrence.state === "cancelled"
                      ? "Cancelled"
                      : "Rescheduled"
                  }
                />
              ) : null}
            </Stack>
            {occurrence.feedback?.content_state === "redacted" ? (
              <Typography color="text.secondary">Feedback removed</Typography>
            ) : occurrence.feedback ? (
              <Typography
                sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {occurrence.feedback.content ?? "Feedback unavailable."}
              </Typography>
            ) : (
              <Typography color="text.secondary">
                No feedback recorded for this session.
              </Typography>
            )}
          </Stack>
        ))
      )}
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          disabled={data.offset <= 0}
          onClick={() => onPage(Math.max(0, data.offset - data.limit))}
        >
          Previous
        </Button>
        <Button
          size="small"
          disabled={data.offset + data.limit >= data.total}
          onClick={() => onPage(data.offset + data.limit)}
        >
          Next
        </Button>
      </Stack>
    </Stack>
  );
}
